import { useCallback, useRef, useState, type KeyboardEvent } from 'react';

interface UseInlineRenameOptions {
  /** The current committed value; seeds the draft each time editing starts. */
  initialValue: string;
  /** Called once per edit session with the draft value. Trimming/blank rules live in the caller. */
  onCommit: (value: string) => void;
}

/**
 * Shared inline-rename interaction used by both the sidebar `FavoriteRow` and the
 * `FavoriteManagerRow`. Encapsulates the edit toggle, draft state, and the keyboard
 * handling (Enter commits, Escape cancels) — including the guard that prevents the
 * commit from firing twice (once from Enter and again from the blur that fires as the
 * input unmounts).
 */
export function useInlineRename({ initialValue, onCommit }: UseInlineRenameOptions) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(initialValue);
  const handledRef = useRef(false);

  const startEditing = useCallback(() => {
    setDraft(initialValue);
    handledRef.current = false;
    setIsEditing(true);
  }, [initialValue]);

  const commit = useCallback(() => {
    if (handledRef.current) return;
    handledRef.current = true;
    onCommit(draft);
    setIsEditing(false);
  }, [draft, onCommit]);

  const cancel = useCallback(() => {
    handledRef.current = true;
    setIsEditing(false);
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        commit();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        cancel();
      }
    },
    [commit, cancel]
  );

  return { isEditing, draft, setDraft, startEditing, commit, cancel, handleKeyDown };
}
